/**
 * Toast. SPEC.md §12.1, §19, §35.
 *
 * Presentational only: it renders one message and at most one action. The queue
 * itself lives in ui-store and is rendered by UndoToast, so the undo snackbar and
 * an error notice are the same object with different copy. §12.1, §37
 *
 * aria-live="polite" rather than "assertive": a committed point change should not
 * interrupt what a screen reader is already saying. §19
 *
 * The vertical padding is deliberately tight. This floats under the top bar
 * (--snackbar-top), over the summary panel's balance line and no further, so the
 * player boxes below it stay readable — and on a 320×568 screen that line is not
 * a tall thing to fit inside. The action and dismiss buttons keep their full
 * 48 px boxes and simply overflow the padding, so §18.4 is untouched and only
 * the empty space goes. §12.1, §18.2, §18.4
 */

import type { ReactNode } from 'react';

export interface ToastProps {
  message: ReactNode;
  actionLabel?: string;
  onAction?: () => void;
  onDismiss?: () => void;
}

export function Toast({ message, actionLabel, onAction, onDismiss }: ToastProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-auto flex w-full items-center gap-3 animate-tick-in rounded-card border border-line bg-raised px-4 py-1.5 text-sm text-ink shadow-card"
    >
      <span className="min-w-0 flex-1 break-words">{message}</span>

      {actionLabel && onAction ? (
        <button
          type="button"
          onClick={onAction}
          className="min-h-tap shrink-0 rounded-control px-3 font-semibold tracking-wide text-accent uppercase transition-opacity active:opacity-70"
        >
          {actionLabel}
        </button>
      ) : null}

      {onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          title="Dismiss"
          className="grid size-tap shrink-0 place-items-center rounded-control text-ink-faint transition-opacity active:opacity-70"
        >
          <span aria-hidden="true">✕</span>
        </button>
      ) : null}
    </div>
  );
}
